import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { whatsappService } from '../services/whatsapp';
import { getApiErrorMessage } from '../utils/apiError';
import type { ConnectWhatsappRequest, SendWhatsappMessageRequest } from '../types/whatsapp';

const statusKey = (organisationId: number) => ['whatsapp-status', organisationId] as const;
export const whatsappMessagesKey = (leadId: number) => ['whatsapp-messages', leadId] as const;
const messagesKey = whatsappMessagesKey;

export function useWhatsappStatus(organisationId: number) {
  return useQuery({
    queryKey: statusKey(organisationId),
    queryFn: () => whatsappService.getStatus(organisationId),
  });
}

export function useConnectWhatsapp(organisationId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (dto: ConnectWhatsappRequest) => whatsappService.connect(organisationId, dto),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: statusKey(organisationId) });
      toast.success('WhatsApp Business account connected');
    },
    onError: (err: unknown) => toast.error(getApiErrorMessage(err, 'Failed to connect WhatsApp')),
  });
}

export function useDisconnectWhatsapp(organisationId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => whatsappService.disconnect(organisationId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: statusKey(organisationId) });
      toast.success('WhatsApp disconnected');
    },
    onError: (err: unknown) => toast.error(getApiErrorMessage(err, 'Failed to disconnect WhatsApp')),
  });
}

export function useLeadMessages(leadId: number | null) {
  return useQuery({
    queryKey: messagesKey(leadId ?? 0),
    queryFn: () => whatsappService.listMessages(leadId!),
    enabled: !!leadId,
  });
}

export function useSendWhatsappMessage(leadId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (dto: SendWhatsappMessageRequest) => whatsappService.sendMessage(leadId, dto),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: messagesKey(leadId) });
    },
    onError: (err: unknown) => toast.error(getApiErrorMessage(err, 'Failed to send message')),
  });
}
